import { StudyService, MediaToken } from "./nebula.service";
import { nebulaClient } from "../../lib/nebulaClient";
import { ICRFData } from "./studiesSlice";

export interface MimeTypeDTO {
    label: string;
    prettyLabel: string;
}

export interface ImagingProcedureDTO {
    orderDate: string;
    visit?: string;
    visitUuid: string;
    procedureIdentifier: string;
    site: string;
    trialName: string;
    studyUid: string;
    identifierType: string;
    reason: string;
    uuid: string;
}

export interface IMediaOctFileDTO {
    attachments: string[];
    mediaDateAndTime: string;
    originalDob: string;
    originalPatientId: string;
    originalPatientFirstName: string;
    originalPatientLastName: string;
    trialName: string;
    fullUploadPath: string;
    uuid: string;
    mimeType: MimeTypeDTO;
    ScanDateAndTime: string;
    ScanPattern: string;
    Laterality: string;
    Manufacturer: string;
    Device: string;
    FileName: string;
    ImageQuality: number;
    ImageQualityDisplayText: string;
    RasterAngleInDeg: number;
    FirstBscanStartXInMm: number;
    FirstBscanStartYInMm: number;
    LastBscanStartXInMm: number;
    LastBscanStartYInMm: number;
    BscanHeightDim: number;
    BscanWidthDim: number;
    NumSlices: number;
    BscanWidthSizeInMicrons: number;
    BscanHeightSizeInMicrons: number;
    NumSlicesSizeInMicrons: number;
    UniqueId: string;
    SiteId: string;
    SeriesId: number;
    Comment: string;
    visit: string;
    ThumbNailFileName: string;
}

export interface VisitDTO {
    imagingProcedure: ImagingProcedureDTO;
    mediaOctFile: IMediaOctFileDTO[];
    crfDataSet: ICRFData;
    hasMedia: boolean;
    isLoadingMedia: boolean;
}

export interface ImagingDataset {
    imagingProcedure: ImagingProcedureDTO;
    mediaOctFile: IMediaOctFileDTO[];
}

export interface EncounterDTO {
    encounter: {
        encounterDate: string;
        encounterIdentifier: string;
        identifierType: string;
        identifierUuid: string;
        encounterUuid: string;
        reason: string;
    };
    imagingDataSet: ImagingDataset[];
}

export interface EncountersDTO {
    patient: {
        comment: string;
        dob: string;
        dod: string;
        birthName: string;
        birthGender: string;
        localId: string;
        isQc: boolean;
    };
    encounters: EncounterDTO[];
    crfDataSet: ICRFData;
}

export const studyService: StudyService = {
    // @ts-ignore
    getPatientStudies: async (patientId: string) => {
        const response = await nebulaClient.get<EncountersDTO>(`/study/encounters/patient/${patientId}`);

        const { crfDataSet, encounters } = response.data as EncountersDTO;

        const enrichImageDataSet = (encounterData: object, imagingDataSetArray: ImagingDataset[]) => {
            return imagingDataSetArray.map((imageDataSet) => {
                return {
                    imagingProcedure: { ...imageDataSet.imagingProcedure, ...encounterData },
                    mediaOctFile: imageDataSet.mediaOctFile.map((media) => ({ ...media, ...encounterData })),
                };
            });
        };

        const studies = encounters
            .map((encounter) =>
                enrichImageDataSet(
                    {
                        encounterUuid: encounter.encounter.encounterUuid,
                        encounterIdentifier: encounter.encounter.encounterIdentifier,
                    },
                    encounter.imagingDataSet
                )
            )
            .flat();

        return { data: { crfs: crfDataSet, studies } };
    },
    getMediaToken: async (expiration?: string) => {
        let url = `/media/token${expiration ? `?expireInSec=${expiration}` : ""}`;

        const response = await nebulaClient.get<MediaToken>(url);

        return response;
    },
    deleteStudy: async (studyId: string) => {
        const response = await nebulaClient.delete<void>(`entity/soft/Media/${studyId}`);

        return response;
    },
    attachFile: async (newFile: FormData) => {
        const response = await nebulaClient.post<void>(`/study/media/attach`, newFile);

        return response;
    },

    downloadStudy: async (studyUrl: string) => {
        const response = await nebulaClient.get<Blob>(studyUrl, { responseType: "blob" });

        return response;
    },
    editNote: async (mediaUuid: string, note: string) => {
        const response = await nebulaClient.post<unknown>("/media/update", { comments: note, mediaUuid });

        return response;
    },
};
